import type { CSSProperties } from "react";
import { theme } from "@duncancooper/brand";

/**
 * Flat SVG versions of the brand logo and icon with a custom wordmark.
 *
 * @duncancooper/brand 0.2.0 dropped the `text` prop from `Logo` and `Icon`
 * (the brand wordmark now ships as fixed vector outlines), so these keep the
 * "td" / "Twice Daily" marks this app used with 0.1.7. The geometry matches
 * that release. Unlike the 3D header logo these render to plain markup, so
 * they also work with renderToStaticMarkup (the favicon).
 */

const FONT_FAMILY = "'Helvetica Neue', 'Helvetica', 'Arial', sans-serif";

export interface FlatLogoProps {
  /** Height in px. Width scales with the viewBox (~92:24 plus separation). */
  size?: number;
  accentColor?: string;
  textColor?: string;
  backgroundColor?: string;
  /** Pushes the semicircles outward, in viewBox units. */
  separation?: number;
  text?: string;
  textOffsetX?: number;
  textOffsetY?: number;
  className?: string;
  style?: CSSProperties;
}

/** Horizontal wordmark flanked by the two brand semicircles. */
export function FlatLogo({
  size = 48,
  accentColor = theme.colors.primary,
  textColor = theme.colors.text,
  backgroundColor = "transparent",
  separation = 0,
  text = "tuit",
  textOffsetX = 0,
  textOffsetY = 0,
  className,
  style,
}: FlatLogoProps) {
  const viewBoxWidth = 92 + 2 * separation;
  const viewBoxX = -separation;
  const width = Math.round(size * (viewBoxWidth / 24));
  const left = 12 - separation;
  const right = 80 + separation;
  return (
    <span className={className} style={{ display: "inline-block", lineHeight: 0, ...style }}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={size}
        overflow="visible"
        viewBox={`${viewBoxX} 0 ${viewBoxWidth} 24`}
        role="img"
        aria-label={`${text} logo`}
      >
        <rect x={0} y={0} width={92} height={24} fill={backgroundColor} />
        <path d={`M ${left} 0 A 12 12 0 0 0 ${left} 24 Z`} fill={accentColor} />
        <path d={`M ${right} 0 A 12 12 0 0 1 ${right} 24 Z`} fill={accentColor} />
        <text
          x={46 + textOffsetX}
          y={23.6 + textOffsetY}
          textAnchor="middle"
          fontFamily={FONT_FAMILY}
          fontSize={33}
          fontWeight={800}
          fill={textColor}
          letterSpacing={-1}
        >
          {text}
        </text>
      </svg>
    </span>
  );
}

export interface FlatIconProps {
  /** Width and height in px. */
  size?: number;
  accentColor?: string;
  textColor?: string;
  backgroundColor?: string;
  /** Pushes the semicircles outward, in viewBox units. */
  separation?: number;
  text?: string;
  className?: string;
  style?: CSSProperties;
}

/** Square icon on a rounded background, suited to favicons and avatars. */
export function FlatIcon({
  size = 48,
  accentColor = theme.colors.primary,
  textColor = theme.colors.text,
  backgroundColor = theme.colors.background,
  separation = 0,
  text = "tuit",
  className,
  style,
}: FlatIconProps) {
  const left = 20 - separation;
  const right = 80 + separation;
  return (
    <span className={className} style={{ display: "inline-block", lineHeight: 0, ...style }}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 100 100"
        role="img"
        aria-label={`${text} icon`}
      >
        <rect width={100} height={100} rx={20} fill={backgroundColor} />
        <path d={`M ${left} 36 A 14 14 0 0 0 ${left} 64 Z`} fill={accentColor} />
        <path d={`M ${right} 36 A 14 14 0 0 1 ${right} 64 Z`} fill={accentColor} />
        <text
          x={50}
          y={62}
          textAnchor="middle"
          fontFamily="Helvetica Neue, Arial, sans-serif"
          fontSize={32}
          fontWeight={800}
          fill={textColor}
          letterSpacing={-1}
        >
          {text}
        </text>
      </svg>
    </span>
  );
}
